import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { API, EMPTY_KB, extractErr } from '../utils';
import { CriteriaEditor } from './CriteriaEditor';

/**
 * Modal for creating a new KB from the tenant portal. Uses the same criteria
 * editor the inline edit form does so the UX stays consistent.
 */
export const NewKBModal = ({ onClose, onCreated, domainCallType }) => {
  const [form, setForm] = useState({ ...EMPTY_KB, call_type: domainCallType || '' });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.name.trim() || !form.call_type.trim()) {
      toast.error('Name and call type are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/knowledge-bases`, form, { timeout: 30000 });
      toast.success('Knowledge base created');
      onCreated();
      onClose();
    } catch (err) {
      console.error('KB create failed', err?.response || err);
      toast.error(extractErr(err, 'Failed to create'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">New Knowledge Base</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="new-kb-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Name</label>
              <input
                value={form.name} placeholder="e.g. New Member Sales"
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:border-primary/60"
                data-testid="new-kb-name"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Call Type (slug)</label>
              <input
                value={form.call_type} placeholder="e.g. sales_new"
                onChange={e => setForm(f => ({ ...f, call_type: e.target.value }))}
                className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:border-primary/60 font-mono"
                data-testid="new-kb-call-type"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wide">Description</label>
            <textarea
              value={form.description} rows={2}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:border-primary/60"
              data-testid="new-kb-description"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wide">Threshold Score</label>
            <input
              type="number" min={0} max={100} value={form.threshold_score}
              onChange={e => setForm(f => ({ ...f, threshold_score: parseInt(e.target.value) || 0 }))}
              className="w-32 bg-white border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:border-primary/60"
              data-testid="new-kb-threshold"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wide mb-1 block">Criteria</label>
            <CriteriaEditor form={form} setForm={setForm} />
            <p className="text-xs text-slate-400 mt-2 italic">
              Tip: upload a rubric document later to override these criteria as the source-of-truth.
            </p>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={create} disabled={saving} data-testid="new-kb-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};
